import React from 'react'
import PropTypes from 'prop-types'
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Typography } from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { Person as PersonIcon } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import MyStethiAIIcon from 'src/components/Icons/MyStethiAIIcon'
import InlineFeedbackDisplay from './InlineFeedbackDisplay'
import JobCard from './JobCard'

const ROLE_USER = 1
const ROLE_ASSISTANT = 3

const ChatMessage = ({ role, content, createdAt, feedback, rankedJobs }) => {
  const theme = useTheme()
  const isUser = role === ROLE_USER

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, md: 1.5 },
          alignItems: 'flex-start',
          flexDirection: isUser ? 'row-reverse' : 'row',
        }}
      >
        {isUser ? (
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
            <PersonIcon sx={{ fontSize: '1.1rem' }} />
          </Avatar>
        ) : (
          <Avatar sx={{ bgcolor: theme.palette.ai.main, width: 32, height: 32 }}>
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.1rem' }} />
          </Avatar>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '85%' }}>
          <Box
            sx={{
              bgcolor: isUser ? 'rgba(42, 181, 116, 0.15)' : theme.palette.grey[100],
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
              {content}
            </Typography>
            {role === ROLE_ASSISTANT && feedback?.map(fb => (
              <InlineFeedbackDisplay key={fb.id} feedback={fb} />
            ))}
          </Box>
          {createdAt && (
            <Typography variant='caption' color='textSecondary' sx={{ mt: 0.5, px: 0.5 }}>
              {createdAt}
            </Typography>
          )}
        </Box>
      </Box>
      {rankedJobs && rankedJobs.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, md: 1.5 },
            alignItems: 'flex-start',
            flexDirection: 'row',
          }}
        >
          <Avatar sx={{ bgcolor: theme.palette.ai.main, width: 32, height: 32 }}>
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.1rem' }} />
          </Avatar>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              flex: 1,
              maxWidth: '85%',
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              bgcolor: theme.palette.grey[50],
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
              <Typography variant='body2' fontWeight={600} color={theme.palette.ai?.dark || theme.palette.secondary.dark}>
                {rankedJobs.length} job{rankedJobs.length !== 1 ? 's' : ''} found
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1.5, pb: 1.5, pt: 0 }}>
              {rankedJobs.map(job => (
                <JobCard key={job.jobId} {...job} />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </>
  )
}

ChatMessage.propTypes = {
  role: PropTypes.number.isRequired,
  content: PropTypes.string,
  createdAt: PropTypes.string,
  feedback: PropTypes.array,
  rankedJobs: PropTypes.array,
}

export default ChatMessage
